import { authRequest } from "@api/auth-requests";
import type { UserPermissions } from "@appType/types";
import {
	AUTH_API_ROUTES,
	AUTH_PRODUCT,
	permissions,
} from "@constants/constants";
import { useSuspenseQuery } from "@tanstack/react-query";
import { partnerIdInstance } from "@utils/partner-id-instance";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { toPermissionNames } from "./gateway-permissions";
import { usePermissionStore } from "./use-permission-store";

/** `/whoami` — the caller as the gateway sees them, across every product. */
interface WhoamiPartner {
	id: string;
	name: string;
	country: string | null;
	role: string | null;
	is_active: boolean | null;
	/** Grants for this partner, as `{ namespace: [action, …] }`. */
	permissions: Record<string, string[]> | null;
}

interface WhoamiProduct {
	id: string;
	name: string;
	partners: WhoamiPartner[] | null;
}

interface WhoamiResponse {
	id: string;
	email: string;
	login: string;
	name: string | null;
	last_name: string | null;
	middle_name: string | null;
	created_at: string;
	products: WhoamiProduct[] | null;
}

export interface PartnerPermissions {
	id: string;
	name: string;
	permissions: string[];
	is_active: boolean;
	role_name: string | null;
}

/** A product the user holds a partner in, with those partners' names. */
export interface ProductAccess {
	/** The gateway's name for the product, as it spells it. */
	name: string;
	partners: string[];
}

export interface ValidateTokenResponse {
	id: string;
	created_at: string;
	login: string;
	first_name: string;
	last_name: string;
	middle_name: string;
	partners: PartnerPermissions[];
	is_root: boolean;
	/** Every product the user holds a partner in, this one included. */
	products: ProductAccess[];
}

const ROOT_ROLE = "root";

/**
 * Partners now hang off the product the session was issued for, and the
 * gateway holds no `is_root` of its own — root is a role carried per partner.
 * `default_language` and `default_currency` are no longer part of identity
 * either; they belong to the scoring service's partner record.
 */
const normalizeWhoami = (data: WhoamiResponse): ValidateTokenResponse => {
	const product = data.products?.find((item) => item.name === AUTH_PRODUCT);

	const partners = (product?.partners ?? []).map((partner) => ({
		id: partner.id,
		name: partner.name,
		is_active: partner.is_active ?? false,
		role_name: partner.role,
		permissions: toPermissionNames(partner.permissions),
	}));

	return {
		id: data.id,
		created_at: data.created_at,
		login: data.login,
		first_name: data.name ?? "",
		last_name: data.last_name ?? "",
		middle_name: data.middle_name ?? "",
		partners,
		is_root: partners.some((partner) => partner.role_name === ROOT_ROLE),
		products: (data.products ?? [])
			.filter((item) => (item.partners?.length ?? 0) > 0)
			.map((item) => ({
				name: item.name,
				partners: (item.partners ?? []).map((partner) => partner.name),
			})),
	};
};

/**
 * Settles which of the user's partners this tab works in, and keeps its id
 * and name in storage: the one another zypl app handed the user over in, if
 * the user has exactly one partner by that name here; else the one stored
 * before, if the user still has it; else the first.
 */
export const settleActivePartner = (
	data: ValidateTokenResponse,
): ValidateTokenResponse => {
	const partnerId = partnerIdInstance.getPartnerId();
	// Matched by name, as the apps know each other's partners by name only
	// (see `beginHandoff`); a name two partners share picks neither.
	const handedOverName = partnerIdInstance.takeHandedOverPartner();
	const named = data.partners.filter(
		(partner) => partner.name === handedOverName,
	);
	const handedOver = named.length === 1 ? named[0] : undefined;
	const stored = data.partners.find((partner) => partner.id === partnerId);
	const active = handedOver ?? stored ?? data.partners.at(0);

	if (active) {
		if (active.id !== partnerId) partnerIdInstance.setPartnerId(active.id);
		// Requests are authorised by name, and a session carried over from
		// before has an id but no name — so it is written on every read.
		partnerIdInstance.setPartnerName(active.name);
	}

	return data;
};

export async function validateToken(): Promise<ValidateTokenResponse> {
	const response = await authRequest.get<WhoamiResponse>(
		AUTH_API_ROUTES.WHOAMI,
	);

	return normalizeWhoami(response.data);
}

export function useValidateToken() {
	const setPermissions = usePermissionStore((state) => state.setPermissions);
	const { t } = useTranslation();
	const { isPending, isFetched, data } = useSuspenseQuery({
		queryFn: () => {
			return validateToken();
		},
		queryKey: ["validateToken"],
		refetchOnWindowFocus: false,
		retry: false,
		// Partner-scoped queries read the id straight from storage, so it has to
		// be settled before this hook's children first render — an effect would
		// leave them a render behind with nothing to re-trigger them.
		// Inline, so it runs on every read rather than once per result.
		select: (data: ValidateTokenResponse) => settleActivePartner(data),
	});

	const handlePermissionsUpdate = useCallback(() => {
		if (!data) return;

		const permissionsKey = permissions(t).map((item) => item.value);
		const permissionsObject = {} as UserPermissions;

		if (data.is_root) {
			permissionsKey.forEach((key) => {
				permissionsObject[key as keyof UserPermissions] = true;
			});
			permissionsObject.root = true;
		} else {
			const activePartner = data.partners.find(
				(item) => item.id === partnerIdInstance.getPartnerId(),
			)?.permissions;

			permissionsKey.forEach((key) => {
				permissionsObject[key as keyof UserPermissions] =
					activePartner?.includes(key) ?? false;
			});
			permissionsObject.root = false;
		}

		setPermissions(permissionsObject);
	}, [data, t, setPermissions]);

	useEffect(() => {
		handlePermissionsUpdate();
	}, [handlePermissionsUpdate]);

	return {
		validateTokenPending: isPending,
		validateTokenFetching: isFetched,
		validateTokenData: data,
	};
}
